<template>
  <div class="select-map-pos-container">
    <div class="map-search">
      <el-input v-model.trim="searchKeyword" placeholder="请输入地点名称搜索定位" clearable @keyup.enter.native="searchPlace">
        <el-button slot="append" :loading="searching" @click="searchPlace">搜索</el-button>
      </el-input>
    </div>
    <div :id="elementId" class="map-container"></div>
  </div>
</template>

<script>
import { getCommunityId } from '@/api/community/communityApi'
import { loadTianditu } from '@/utils/tianditu'
import { storeToTianditu, tiandituToStore } from '@/utils/coordTransform'

const DEFAULT_LNG = 116.397128
const DEFAULT_LAT = 39.916527

export default {
  name: 'SelectMapPos',
  props: {
    elementId: {
      type: String,
      default: 'selectMap'
    },
    initData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      map: null,
      marker: null,
      communityId: '',
      storeCoord: 'GCJ02',
      searchKeyword: '',
      searching: false
    }
  },
  mounted() {
    this.communityId = getCommunityId()
  },
  beforeDestroy() {
    this.destroyMap()
  },
  methods: {
    /**
     * 初始化天地图选点。
     * @param {Object} data 传入数据库坐标 { lng, lat }，按 STORE_COORD 自动反解为 WGS84 显示
     */
    initMap(data = {}) {
      this.destroyMap()
      loadTianditu()
        .then(({ T, config }) => {
          this.storeCoord = config.STORE_COORD || 'GCJ02'
          const storeLng = data.lng ? parseFloat(data.lng) : DEFAULT_LNG
          const storeLat = data.lat ? parseFloat(data.lat) : DEFAULT_LAT
          const display = storeToTianditu(storeLng, storeLat, this.storeCoord)
          const dom = document.getElementById(this.elementId)
          if (!dom) {
            return
          }
          const map = new T.Map(dom)
          map.centerAndZoom(new T.LngLat(display.lng, display.lat), 16)
          map.addControl(new T.Control.Zoom())
          map.addControl(new T.Control.Scale())
          this.enableMapInteractions(map)

          if (data.lng && data.lat) {
            this.placeMarker(T, map, display.lng, display.lat)
          }

          map.addEventListener('click', evt => {
            const lnglat = evt.lnglat
            if (!lnglat) return
            const wgsLng = lnglat.getLng()
            const wgsLat = lnglat.getLat()
            this.placeMarker(T, map, wgsLng, wgsLat)
            const store = tiandituToStore(wgsLng, wgsLat, this.storeCoord)
            this.$emit('position-change', {
              lng: store.lng,
              lat: store.lat,
              lngDisplay: wgsLng,
              latDisplay: wgsLat,
              storeCoord: this.storeCoord
            })
          })
          this.map = map
        })
        .catch(err => {
          this.$message && this.$message.error(err.message || '地图初始化失败')
        })
    },
    placeMarker(T, map, lng, lat) {
      const point = new T.LngLat(lng, lat)
      const icon = new T.Icon({
        iconUrl: '/img/maper.png',
        iconSize: new T.Point(25, 35),
        iconAnchor: new T.Point(12, 35)
      })
      if (this.marker) {
        this.marker.setLngLat(point)
        this.marker.setIcon(icon)
      } else {
        this.marker = new T.Marker(point, { icon })
        map.addOverLay(this.marker)
      }
    },
    enableMapInteractions(map) {
      const interactionMethods = [
        'enableDrag',
        'enableScrollWheelZoom',
        'enableDoubleClickZoom',
        'enableKeyboard',
        'enableInertia'
      ]
      interactionMethods.forEach(method => {
        if (typeof map[method] === 'function') {
          map[method]()
        }
      })
    },
    searchPlace() {
      const keyword = (this.searchKeyword || '').trim()
      if (!keyword) {
        this.$message && this.$message.warning('请输入地点名称')
        return
      }
      if (!this.map || !window.T || !window.T.LocalSearch) {
        this.$message && this.$message.warning('地图尚未初始化完成，请稍后再试')
        return
      }

      this.searching = true
      const localSearch = new window.T.LocalSearch(this.map, {
        pageCapacity: 10,
        onSearchComplete: result => {
          this.searching = false
          const point = this.getFirstSearchPoint(result)
          if (!point) {
            this.$message && this.$message.warning('未找到相关地点')
            return
          }
          this.applyDisplayPosition(point.lng, point.lat, 17)
        }
      })
      try {
        localSearch.search(keyword)
      } catch (e) {
        this.searching = false
        this.$message && this.$message.error('地点搜索失败，请稍后再试')
      }
    },
    getFirstSearchPoint(result) {
      const pois = result && typeof result.getPois === 'function' ? result.getPois() : []
      if (!pois || pois.length < 1) {
        return null
      }
      for (let i = 0; i < pois.length; i++) {
        const point = this.normalizeSearchPoint(pois[i] && (pois[i].lonlat || pois[i].lnglat || pois[i].point || pois[i]))
        if (point) {
          return point
        }
      }
      return null
    },
    normalizeSearchPoint(point) {
      if (!point) {
        return null
      }
      if (typeof point.getLng === 'function' && typeof point.getLat === 'function') {
        return {
          lng: point.getLng(),
          lat: point.getLat()
        }
      }
      if (typeof point === 'string') {
        const parts = point.split(/[,\s]+/).map(item => parseFloat(item)).filter(item => !Number.isNaN(item))
        if (parts.length >= 2) {
          return {
            lng: parts[0],
            lat: parts[1]
          }
        }
      }
      const lng = point.lng || point.lon || point.longitude
      const lat = point.lat || point.latitude
      if (lng && lat) {
        return {
          lng: parseFloat(lng),
          lat: parseFloat(lat)
        }
      }
      return null
    },
    applyDisplayPosition(wgsLng, wgsLat, zoom = 17) {
      const point = new window.T.LngLat(wgsLng, wgsLat)
      this.map.centerAndZoom(point, zoom)
      this.placeMarker(window.T, this.map, wgsLng, wgsLat)
      const store = tiandituToStore(wgsLng, wgsLat, this.storeCoord)
      this.$emit('position-change', {
        lng: store.lng,
        lat: store.lat,
        lngDisplay: wgsLng,
        latDisplay: wgsLat,
        storeCoord: this.storeCoord
      })
    },
    /**
     * 兼容旧调用：传入数据库坐标，更新地图与标记位置。
     */
    updatePosition(lat, lng) {
      if (!this.map || !window.T) return
      const display = storeToTianditu(lng, lat, this.storeCoord)
      const point = new window.T.LngLat(display.lng, display.lat)
      this.map.centerAndZoom(point, 17)
      this.placeMarker(window.T, this.map, display.lng, display.lat)
    },
    destroyMap() {
      if (this.map) {
        try {
          if (this.marker) {
            this.map.removeOverLay(this.marker)
          }
          if (typeof this.map.clearOverLays === 'function') {
            this.map.clearOverLays()
          }
          if (typeof this.map.destroy === 'function') {
            this.map.destroy()
          }
        } catch (e) {
          // ignore
        }
      }
      const dom = document.getElementById(this.elementId)
      if (dom) {
        dom.innerHTML = ''
      }
      this.marker = null
      this.map = null
    }
  }
}
</script>

<style scoped>
.select-map-pos-container {
  width: 100%;
  height: 100%;
}

.map-container {
  width: 100%;
  height: 500px;
  cursor: grab;
  user-select: none;
}

.map-container:active {
  cursor: grabbing;
}

.map-search {
  margin-bottom: 10px;
  width: 420px;
}
</style>
